import jsPDF from "jspdf";
import "jspdf-autotable";

const COLORS = {
  primary: { main: [41, 128, 185] },
  text: { dark: [44, 62, 80], light: [236, 240, 241] },
};

// Function to format rector comments with bold text and proper spacing
const formatRectorCommentsForPDF = (doc, comments, startX, startY, maxWidth) => {
  if (!comments) return startY;

  let currentY = startY;
  const lines = comments.split('\n');

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Check for lines that should be bold
    const isBoldLine =
      trimmedLine.includes('MAONI YA RECTOR') ||
      trimmedLine.includes('MAONI YA GOMBERA') ||
      trimmedLine.includes('TABIA YA MSEMINARI') ||
      trimmedLine.includes('1. Wito Kwa Wazazi na Walezi') ||
      trimmedLine.includes('2. Wito Kwa Waseminari') ||
      trimmedLine.includes('3. Tarehe ya Kikao kwa Wazazi') ||
      trimmedLine.includes('4. Tarehe ya Kufungua Seminari') ||
      trimmedLine.includes('Fr Philip Tairo') ||
      trimmedLine.includes('Rector');

    // Set font style based on content
    if (isBoldLine) {
      doc.setFont("helvetica", "bold");
    } else {
      doc.setFont("helvetica", "normal");
    }

    // Handle bullet points
    if (trimmedLine.startsWith('•')) {
      const bulletText = trimmedLine.substring(1).trim();
      const wrappedLines = doc.splitTextToSize(bulletText, maxWidth - 10);

      // Draw bullet point
      doc.setFont("helvetica", "bold");
      doc.text('•', startX, currentY + 3);
      doc.setFont("helvetica", "normal");

      // Draw text
      doc.text(wrappedLines, startX + 4, currentY);
      currentY += wrappedLines.length * 5;
    } else {
      // Regular text
      const wrappedLines = doc.splitTextToSize(trimmedLine, maxWidth);
      doc.text(wrappedLines, startX, currentY);
      currentY += wrappedLines.length * 5;
    }

    // Add extra space after bold lines
    if (isBoldLine) {
      currentY += 2;
    }

    // Add space between paragraphs
    if (index < lines.length - 1 && trimmedLine !== '') {
      currentY += 1;
    }
  });

  return currentY;
};

/**
 * @param {object} report - Evaluation report data
 * @param {{ returnBlob?: boolean }} options - If returnBlob is true, returns PDF Blob instead of saving (for print).
 * @returns {string | Blob | undefined} - File name when saving, or Blob when returnBlob is true.
 */
const generateStudentEvaluationReport = (report, options = {}) => {
  if (!report) return;

  try {
    const doc = new jsPDF("portrait", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const MARGIN_LEFT = 12;
    const MARGIN_RIGHT = 12;
    const CONTENT_WIDTH = pageWidth - MARGIN_LEFT - MARGIN_RIGHT;
    let currentY = 14;

    // ===== HEADER =====
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("ST. MARY'S JUNIOR SEMINARY - VISIGA", pageWidth / 2, currentY, {
      align: "center",
    });

    currentY += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("TATHMINI YA MWENDENDO WA MSEMINARI", pageWidth / 2, currentY, {
      align: "center",
    });

    currentY += 6;
    doc.setDrawColor(...COLORS.primary.main);
    doc.setLineWidth(0.3);
    doc.line(MARGIN_LEFT, currentY, pageWidth - MARGIN_RIGHT, currentY);

    // ===== STUDENT INFORMATION =====
    currentY += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("TAARIFA ZA MSEMINARI", MARGIN_LEFT, currentY);

    currentY += 6;
    const info = [
      ["Jina la Mseminari:", report.studentName || "HAIJAKABIDHIWA"],
      ["Muhula:", report.termName || "HAIJAKABIDHIWA"],
    ];

    doc.setFontSize(9);
    info.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, MARGIN_LEFT, currentY);
      doc.setFont("helvetica", "normal");
      doc.text(value, MARGIN_LEFT + 41, currentY);
      currentY += 4.5;
    });

    currentY += 2;

    // ===== EVALUATION DATA =====
    const sections = [
      {
        title: "MAISHA YA KIROHO",
        field: "spiritualLife",
        items: [
          "Ushiriki wake katika Misa, Ibada mbalimbali.",
          "Ushiriki katika vikundi vya sala.",
          "Kuongoza sala, kuimba, kusoma Neno la Mungu.",
          "Nidhamu na heshima kanisani.",
        ],
      },
      {
        title: "MAISHA YA KITAALUMA",
        field: "academicLife",
        items: [
          "Kuhudhuria darasani na kutumia muda vizuri.",
          "Kufanya mazoezi na majaribio kwa wakati.",
          "Umakini na ushiriki darasani.",
          "Bidii ya kuzungumza Kiingereza.",
        ],
      },
      {
        title: "KAZI ZA MIKONO",
        field: "manualWork",
        items: [
          "Kufika kazini kwa wakati.",
          "Bidii na juhudi katika kazi.",
          "Kufanya kazi nzuri.",
          "Moyo wa kujitolea.",
          "Utiifu katika kazi.",
        ],
      },
      {
        title: "UTU",
        field: "behaviour",
        items: [
          "Uaminifu.",
          "Utunzaji wa mali.",
          "Heshima na ushirikiano.",
          "Utii kwa maagizo.",
          "Kukubali kosa.",
          "Kuzingatia ratiba ya seminari.",
          "Uvaaji wa sare rasmi.",
        ],
      },
      {
        title: "AFYA",
        field: "health",
        items: [
          "Usafi wa mwili na mavazi.",
          "Kwenda zahanati anapoumwa.",
          "Kutumia dawa kwa wakati.",
        ],
      },
      {
        title: "UONGOZI",
        field: "leadershipSkills",
        items: [
          "Kipaji cha uongozi.",
          "Kushawishi wenzake kufuata sheria.",
          "Kushirikiana na viongozi.",
        ],
      },
      {
        title: "MICHEZO",
        field: "sports",
        items: [
          "Kushiriki michezo.",
          "Kushiriki sanaa.",
          "Kutunza vifaa vya michezo.",
        ],
      },
    ];

    // 3 columns compressed
    const columns = 3;
    const marginX = MARGIN_LEFT;
    const sectionWidth = (pageWidth - MARGIN_LEFT - MARGIN_RIGHT - (columns - 1) * 4) / columns;

    for (let i = 0; i < sections.length; i += columns) {
      if (currentY > pageHeight - 70) {
        doc.addPage();
        currentY = 12;
      }

      let maxHeight = 0;
      const row = sections.slice(i, i + columns);

      row.forEach((section) => {
        const height =
          section.items.reduce((sum, item) => {
            return sum + doc.splitTextToSize(item, sectionWidth - 8).length * 2.4;
          }, 0) + 14;

        maxHeight = Math.max(maxHeight, height);
      });

      row.forEach((section, index) => {
        const x = marginX + index * (sectionWidth + 4);
        const y = currentY;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.text(section.title, x + 1, y + 1);

        doc.rect(x, y + 2, sectionWidth, maxHeight);

        let textY = y + 7;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);

        section.items.forEach((item) => {
          const wrapped = doc.splitTextToSize(`• ${item}`, sectionWidth - 6);
          doc.text(wrapped, x + 3, textY);
          textY += wrapped.length * 2.4;
        });

        const grade = report[section.field] || "?";

        // Grade circle
        const gradeX = x + sectionWidth - 8;
        const gradeY = y + maxHeight - 8;

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.7);
        doc.circle(gradeX, gradeY, 4.5);

        if (grade === "A") doc.setFillColor(76, 175, 80);
        else if (grade === "B") doc.setFillColor(33, 150, 243);
        else if (grade === "F") doc.setFillColor(244, 67, 54);
        else doc.setFillColor(120, 120, 120);

        doc.circle(gradeX, gradeY, 4.5, "F");

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text(grade, gradeX, gradeY + 1.7, { align: "center" });
        doc.setTextColor(0, 0, 0);
      });

      currentY += maxHeight + 8;
    }

    // ===== GRADE LEGEND =====
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("MAANA YA MADARAJA", MARGIN_LEFT, currentY);
    currentY += 5;

    const legend = [
      { grade: "A", meaning: "Vizuri", color: [76, 175, 80] },
      { grade: "B", meaning: "Wastani", color: [33, 150, 243] },
      { grade: "F", meaning: "Dhaifu", color: [244, 67, 54] },
    ];

    legend.forEach((item, i) => {
      const x = MARGIN_LEFT + i * 50;
      const y = currentY + 2;

      doc.circle(x, y, 4);
      doc.setFillColor(...item.color);
      doc.circle(x, y, 4, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.text(item.grade, x, y + 1.6, { align: "center" });
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(8);
      doc.text(`= ${item.meaning}`, x + 8, y + 1.6);
    });

    currentY += 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("ZINGATIA:", MARGIN_LEFT, currentY);
    currentY += 5;

    const explanation =
      "Kwa mujibu wa sheria na taratibu za seminari, Mseminari kupata “F” kwenye kipengele cha maisha ya kiroho au tabia au kupata F(2) kutoka kwenye vipengele vingine vya kimalezi,ni dalili mbaya ya muendelezo wa malezi yake seminarini.";
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const explanationLines = doc.splitTextToSize(explanation, CONTENT_WIDTH);
    doc.text(explanationLines, MARGIN_LEFT, currentY);
    currentY += explanationLines.length * 3.5 + 4;

    // ===== MAONI YA RECTOR =====
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("MAONI YA RECTOR", pageWidth / 2, currentY, { align: "center" });
    currentY += 8;

    // 1. Wito Kwa Wazazi na Walezi
    doc.setFontSize(10);
    doc.text("1. Wito Kwa Wazazi na Walezi", MARGIN_LEFT, currentY);
    currentY += 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);

    const wazaziText =
      "Kumuelekeza katika uwajibikaji wa nyumbani na kumlea katika maadili mema, kumsaidia kudumisha sala, unyenyekevu, kazi na nidhamu, tukikumbuka kuwa maisha ya seminari yanaimarika pale ambapo malezi ya nyumbani yanajengwa kwa uzito na umakini.";

    const wazaziLines = doc.splitTextToSize(wazaziText, CONTENT_WIDTH);
    doc.text(wazaziLines, MARGIN_LEFT, currentY);
    currentY += wazaziLines.length * 4 + 3;

    // 2. Wito Kwa Waseminari
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("2. Wito Kwa Waseminari", MARGIN_LEFT, currentY);
    currentY += 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);

    const waseminariText =
      "Likizo si mapumziko kutoka maisha ya sala, nawasihi kwa dhati kushiriki jumuiya, misa za asubuhi, Dominika na shughuli mbalimbali parokiani, pamoja na kutunza adabu, nidhamu na utulivu popote mlipo.";

    const waseminariLines = doc.splitTextToSize(waseminariText, CONTENT_WIDTH);
    doc.text(waseminariLines, MARGIN_LEFT, currentY);
    currentY += waseminariLines.length * 4 + 4;

    // TABIA YA MSEMINARI
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("TABIA YA MSEMINARI:", MARGIN_LEFT, currentY);
    currentY += 7;

    const savedRectorComments = report.rectorComments
      ? String(report.rectorComments).replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim()
      : "";

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);

    if (savedRectorComments) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      currentY = formatRectorCommentsForPDF(
        doc,
        savedRectorComments,
        MARGIN_LEFT,
        currentY,
        CONTENT_WIDTH
      );
    } else {
      for (let i = 0; i < 5; i++) {
        doc.line(MARGIN_LEFT, currentY, pageWidth - MARGIN_RIGHT, currentY);
        currentY += 6;
      }
    }

    currentY += 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Rector", pageWidth / 2, currentY, { align: "center" });

    currentY += 5;

    doc.setFontSize(9.5);
    doc.text("Visiga Seminary", pageWidth / 2, currentY, { align: "center" });

    // ===== FINANCIAL INFO PAGE =====
    const financialData = [];
    const financialFields = [
      { key: "debts", label: "Deni la muhula uliopita" },
      { key: "firstTermFee", label: "Ada ya muhula wa kwanza" },
      { key: "secondTermFee", label: "Ada ya muhula wa pili" },
      { key: "firstTermExamFee", label: "Mchango wa Mitihani - Term 1" },
      { key: "secondTermExamFee", label: "Mchango wa Mitihani - Term 2" },
      { key: "firstTermOtherContribution", label: "Michango mingine - Term 1" },
      { key: "secondTermOtherContribution", label: "Michango mingine - Term 2" },
    ];

    // NEW RULE: include ONLY values greater than 0
    financialFields.forEach((f) => {
      const value = Number(report[f.key]);

      if (!isNaN(value) && value > 0) {
        financialData.push([f.label, value.toLocaleString() + " /="]);
      }
    });

    if (financialData.length > 0) {
      doc.addPage();
      currentY = 20;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("TAARIFA ZA KIFEDHA", pageWidth / 2, currentY, {
        align: "center",
      });

      currentY += 8;

      doc.autoTable({
        startY: currentY,
        margin: { left: MARGIN_LEFT, right: MARGIN_RIGHT },
        tableWidth: CONTENT_WIDTH,
        head: [["Maelezo", "Kiasi (TZS)"]],
        body: financialData,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: CONTENT_WIDTH * 0.65, fontStyle: "bold" },
          1: { cellWidth: CONTENT_WIDTH * 0.35, halign: "center", fontStyle: "bold" },
        },
      });

      currentY = doc.lastAutoTable.finalY + 10;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("MAELEKEZO YA MALIPO:", MARGIN_LEFT, currentY);

      currentY += 6;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const instructions = [
        "• Malipo yote yafanyike kwenye akaunti ya seminari",
        "• Hakikisha unapokea risiti ya malipo",
        "• Wasiliana na ofisi ya fedha: 0719888509",
        "• Ada hulipwa kabla ya kuanza kwa muhula",
      ];

      instructions.forEach((line) => {
        const wrapped = doc.splitTextToSize(line, CONTENT_WIDTH);
        doc.text(wrapped, MARGIN_LEFT, currentY);
        currentY += wrapped.length * 4.5;
      });
    }

    // ===== FOOTER =====
    const totalPages = doc.internal.getNumberOfPages();
    for (let n = 1; n <= totalPages; n++) {
      doc.setPage(n);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(7);

      doc.text(
        "St. Mary's Junior Seminary - Visiga | Ripoti ya Siri ya Mseminari",
        pageWidth / 2,
        pageHeight - 8,
        { align: "center" }
      );

      doc.text(`Ukurasa ${n} / ${totalPages}`, pageWidth / 2, pageHeight - 4, {
        align: "center",
      });
    }

    const fileName = `Tathmini_Mseminari_${report.studentName?.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "") ||
      "Haijajulikana"
      }.pdf`;

    if (options.returnBlob) {
      return doc.output("blob");
    }
    doc.save(fileName);
    return fileName;
  } catch (err) {
    console.error("Error generating PDF:", err);
  }
};

export default generateStudentEvaluationReport;